import { useState, useMemo } from 'react';
import { X, Calendar, Clock, Check } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

/**
 * DateTimePickerModal - Constrained date/time picker
 * - Shows next 7 days for booking, up to 30 days for visits
 * - 15-minute increments for time
 * - Service length selection (optional)
 */
export function DateTimePickerModal({ 
  isOpen, 
  onClose, 
  onSelect,
  showServiceSelect = true,
  initialService = 'HH',
  maxDays = 7 // 7 for booking, can be 30 for orientation visits
}) {
  const { language } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedService, setSelectedService] = useState(initialService);

  const labels = {
    en: {
      title: 'Select Date & Time',
      selectDay: 'Select Day',
      selectTime: 'Select Time',
      selectService: 'Session Length',
      confirm: 'Confirm Selection',
      today: 'Today',
      tomorrow: 'Tomorrow',
      services: {
        QV: 'Quick Visit (15 min)',
        HH: 'Half Hour (30 min)',
        HR: 'Full Hour (60 min)',
        CUSTOM: 'Custom Request'
      },
      days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    },
    es: {
      title: 'Seleccionar Fecha y Hora',
      selectDay: 'Seleccionar Día',
      selectTime: 'Seleccionar Hora',
      selectService: 'Duración de Sesión',
      confirm: 'Confirmar Selección',
      today: 'Hoy',
      tomorrow: 'Mañana',
      services: {
        QV: 'Visita Rápida (15 min)',
        HH: 'Media Hora (30 min)',
        HR: 'Hora Completa (60 min)',
        CUSTOM: 'Solicitud Especial'
      },
      days: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
      months: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    }
  };

  const t = labels[language];

  // Generate next days based on maxDays prop
  const nextDays = useMemo(() => {
    const days = [];
    const today = new Date();
    const dayNames = t.days;
    const monthNames = t.months;
    
    for (let i = 0; i < maxDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push({
        date,
        dayName: dayNames[date.getDay()],
        dayNum: date.getDate(),
        month: monthNames[date.getMonth()],
        isToday: i === 0,
        isTomorrow: i === 1,
      });
    }
    return days;
  }, [t.days, t.months, maxDays]);

  // Generate time slots (15-minute increments)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const h12 = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const display = `${h12}:${min.toString().padStart(2, '0')} ${ampm}`;
        const value = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        slots.push({ display, value });
      }
    }
    return slots;
  }, []);

  // Filter time slots based on selected date (if today, only show future times)
  const availableTimeSlots = useMemo(() => {
    if (!selectedDate) return timeSlots;
    
    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();
    
    if (!isToday) return timeSlots;
    
    const currentHour = today.getHours();
    const currentMin = today.getMinutes();
    
    return timeSlots.filter(slot => {
      const [h, m] = slot.value.split(':').map(Number);
      return h > currentHour || (h === currentHour && m > currentMin);
    });
  }, [selectedDate, timeSlots]);

  const handleConfirm = () => {
    if (!selectedDate || !selectedTime) return;
    
    const day = nextDays.find(d => d.date.toDateString() === selectedDate.toDateString());
    const time = timeSlots.find(t => t.value === selectedTime);
    
    const formatted = `${day.dayName} · ${day.month} ${day.dayNum} · ${time.display}`;
    const isoDate = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}T${selectedTime}`;
    
    onSelect({
      formatted,
      isoDate,
      service: selectedService,
      date: selectedDate,
      time: selectedTime
    });
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div 
        className="bg-[#111022] rounded-2xl border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#111022]">
          <h3 className="font-heading text-lg font-bold text-[#F4F2FF] flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#8B5CFF]" />
            {t.title}
          </h3>
          <button onClick={onClose} className="text-[#A7A3BA] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Day Selection */}
          <div>
            <p className="text-sm text-[#A7A3BA] mb-3">{t.selectDay}</p>
            <div className={`grid gap-2 ${maxDays <= 7 ? 'grid-cols-7' : 'grid-cols-7 max-h-48 overflow-y-auto'}`}>
              {nextDays.map((day, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedDate(day.date)}
                  className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                    selectedDate?.toDateString() === day.date.toDateString()
                      ? 'bg-[#FF4DA6] text-white'
                      : 'bg-[#0B0A12] border border-white/10 text-[#A7A3BA] hover:border-[#FF4DA6]/50'
                  }`}
                >
                  <span className="text-[10px] uppercase tracking-wider opacity-75">
                    {day.isToday ? t.today : day.isTomorrow ? t.tomorrow : day.dayName}
                  </span>
                  <span className="text-lg font-bold">{day.dayNum}</span>
                  <span className="text-[10px]">{day.month}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Time Selection */}
          {selectedDate && (
            <div>
              <p className="text-sm text-[#A7A3BA] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {t.selectTime}
              </p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-48 overflow-y-auto pr-2">
                {availableTimeSlots.map((slot, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedTime(slot.value)}
                    className={`py-2 px-1 text-xs rounded-lg transition-all ${
                      selectedTime === slot.value
                        ? 'bg-[#8B5CFF] text-white'
                        : 'bg-[#0B0A12] border border-white/10 text-[#A7A3BA] hover:border-[#8B5CFF]/50'
                    }`}
                  >
                    {slot.display}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Service Selection */}
          {showServiceSelect && selectedTime && (
            <div>
              <p className="text-sm text-[#A7A3BA] mb-3">{t.selectService}</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(t.services).map(([code, label]) => (
                  <button
                    key={code}
                    onClick={() => setSelectedService(code)}
                    className={`py-3 px-4 text-sm rounded-xl transition-all ${
                      selectedService === code
                        ? 'bg-[#27D7FF]/20 border border-[#27D7FF] text-[#27D7FF]'
                        : 'bg-[#0B0A12] border border-white/10 text-[#A7A3BA] hover:border-[#27D7FF]/50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 sticky bottom-0 bg-[#111022]">
          <button
            onClick={handleConfirm}
            disabled={!selectedDate || !selectedTime}
            className="btn-neon btn-neon-pink w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
            <span>{t.confirm}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default DateTimePickerModal;
